/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2022 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */

import { useContext, useState } from 'react'

import {
  CryptographyWrapper,
  useCryptographyStore,
} from '@builddocs/cryptography-js'
import {
  getPoaDataToSign,
  PoaItem,
  SignatureState,
  useCheckOperation,
  useGetMRFList,
  User,
  useVersionOf,
} from '@builddocs/data-management'
import {
  FaceControlContext,
  SignHandlerParams,
} from '@builddocs/feature-faceid'
import {
  ApplicationKeys,
  DocumentSigningTask,
  // logToSectionFourForStamps,
  prepareStamp,
  SignerInStampType,
  signStamp,
  useTasksOfDocument,
  useTasksSignStamps,
} from '@builddocs/filestore-api'
import { DeviationDialog } from '@builddocs/mui'
import { useToast } from '@builddocs/system'
import { Button, Stack } from '@mui/material'
import { AxiosError } from 'axios'
import format from 'date-fns/format'

export interface SignPanelProps {
  user: User
  docId: string
  signingTask: DocumentSigningTask
  userSigner: SignerInStampType
  documentType: ApplicationKeys
  onUpdatePDF?: () => void
}

export function SignPanel({
  user,
  docId,
  signingTask,
  userSigner,
  documentType,
  onUpdatePDF,
}: SignPanelProps) {
  const { cryptography_version, poaIsEnabled } = useVersionOf()
  const { toast } = useToast()
  const { selectedCertificate } = useCryptographyStore()
  const [proxy, setProxy] = useState<PoaItem | null>(null)
  const { data: mrfList } = useGetMRFList({
    userId: user.userId,
    enabled: poaIsEnabled,
  })

  const { refresh: refreshTaskSigningStamps } = useTasksSignStamps(
    user.employment.employmentId,
  )

  const { refresh: refreshTasksOfDocument } = useTasksOfDocument(docId)

  const faceControl = useContext(FaceControlContext)

  const prepareData = async () => {
    if (!selectedCertificate) return
    const poaData = getPoaDataToSign({
      proxyId: proxy?.poaIdInternal,
      poa: proxy,
      poaIsEnabled,
    })

    try {
      const result = await prepareStamp({
        base64signature: null,
        documentId: docId,
        employmentId: userSigner.employmentId,
        imprint: selectedCertificate.Thumbprint,
        keyIssuer: selectedCertificate.Issuer.CN,
        name: selectedCertificate.Name,
        signatureState: SignatureState.SIGNED,
        stampTemplateId: signingTask.stampTemplateId,
        certificateDateStart: format(
          selectedCertificate.ValidFromDate,
          'yyyy-MM-dd',
        ),
        certificateDateEnd: format(
          selectedCertificate.ValidToDate,
          'yyyy-MM-dd',
        ),
        faceId: {
          keySerialNumber: selectedCertificate.SerialNumber,
        },
        ...poaData,
      })

      if (!result) {
        toast.error('Ошибка при подготовке данных для подписания')
        return
      }

      return result
    } catch (error) {
      console.error('Error preparing data:', error)
      toast.error('Ошибка при подготовке данных для подписания')
    }
  }

  const signedDataTransform = async (
    data: string[],
    proxyId: string | null,
  ) => {
    const poa = mrfList?.poas?.find(poa => poa?.poaIdInternal === proxyId)
    const poaData = getPoaDataToSign({ proxyId, poa, poaIsEnabled })

    try {
      await signStamp({
        base64signature: data[0],
        documentId: docId,
        employmentId: userSigner.employmentId,
        imprint: selectedCertificate?.Thumbprint || '',
        keyIssuer: selectedCertificate?.Issuer.CN || '',
        signatureState: SignatureState.SIGNED,
        stampTemplateId: signingTask.stampTemplateId,
        certificateDateStart: format(
          selectedCertificate?.ValidFromDate || new Date(),
          'yyyy-MM-dd',
        ),
        certificateDateEnd: format(
          selectedCertificate?.ValidToDate || new Date(),
          'yyyy-MM-dd',
        ),
        faceId: {
          keySerialNumber: selectedCertificate?.SerialNumber || '',
        },
        ...poaData,
      })

      toast.success('Документ подписан')
      refreshTasksOfDocument()
      refreshTaskSigningStamps()
      onUpdatePDF && onUpdatePDF()
    } catch (error) {
      console.error('Error signing document:', error)
      toast.error('Ошибка при подписании документа')
    }
  }

  const handleSign = () => {
    // Открываем диалог верификации и передаём коллбэк для подписи
    faceControl.openFaceControl({
      isEmbedded: true,
      getDigestHandler: async ({
        signedHash,
        keyIssuer,
        signerName,
        imprint,
        facePhoto,
        keySerialNumber,
        certificateDateStart,
        certificateDateEnd,
        withPoa,
        poaId,
        poaDateStart,
        poaDateEnd,
      }) => {
        const poa = mrfList?.poas?.find(poa => poa?.poaIdInternal === poaId)
        const poaData = getPoaDataToSign({
          proxyId: poaId,
          poa,
          poaIsEnabled,
          withPoa,
          poaDateStart,
          poaDateEnd,
        })

        setProxy(poa ?? null)
        return {
          hash: await prepareStamp({
            base64signature: signedHash,
            documentId: docId,
            employmentId: userSigner.employmentId,
            imprint: imprint,
            keyIssuer: keyIssuer,
            name: signerName,
            signatureState: SignatureState.SIGNED,
            stampTemplateId: signingTask.stampTemplateId,
            certificateDateStart,
            certificateDateEnd,
            faceId: {
              faceImage: facePhoto,
              keySerialNumber,
            },
            ...poaData,
          }),
        }
      },
      signHandler: async ({
        signedHash,
        keyIssuer,
        imprint,
        facePhoto,
        keySerialNumber,
        certificateDateStart,
        certificateDateEnd,
        withPoa,
        poaId,
        poaDateStart,
        poaDateEnd,
      }: SignHandlerParams) => {
        const poa = mrfList?.poas?.find(poa => poa?.poaIdInternal === poaId)
        const poaData = getPoaDataToSign({
          proxyId: poaId,
          poa,
          poaIsEnabled,
          withPoa,
          poaDateStart,
          poaDateEnd,
        })

        setProxy(poa ?? null)
        const res = await signStamp({
          base64signature: signedHash,
          documentId: docId,
          employmentId: userSigner.employmentId,
          imprint: imprint,
          keyIssuer: keyIssuer,
          signatureState: SignatureState.SIGNED,
          stampTemplateId: signingTask.stampTemplateId,
          certificateDateStart,
          certificateDateEnd,
          faceId: {
            faceImage: facePhoto,
            keySerialNumber,
          },
          ...poaData,
        })

        return res
      },
      onSuccess: res => {
        if (res.status === 200) {
          refreshTasksOfDocument()
          refreshTaskSigningStamps()
          toast.success('Документ подписан')
          onUpdatePDF && onUpdatePDF()
        }
        console.debug('Получен дайджест документа')
      },
      onFailure: error => {
        if (error.response && error.response.status === 401) {
          toast.error(`Не удалось подтвердить вашу личность!`)
        } else {
          const e = error as AxiosError

          toast.error(
            e?.response?.data?.message ??
              `Не удалось подписать! Ошибка ${error.response?.status}`,
          )
        }
      },
    })
  }

  const onRejectStamp = async args => {
    const poaData = getPoaDataToSign({
      proxyId: proxy?.poaIdInternal,
      poa: proxy,
      poaIsEnabled,
    })

    try {
      await signStamp({
        documentId: docId,
        employmentId: userSigner.employmentId,
        signatureState: SignatureState.REJECTED,
        stampTemplateId: signingTask.stampTemplateId,
        comment: args[0].problemDescription,
        ...poaData,
      })
      refreshTasksOfDocument()
      refreshTaskSigningStamps()
      toast.success('Штамп отклонён')
      // TODO бекенду необходимо убрать contractId из запроса, на данном уровне contractId неизвестен
      // await logToSectionFourForStamps({
      //   statusStamp,
      //   contractId: '',
      //   body: args[0],
      //   user,
      //   additionalInfo: args[1],
      //   onSuccess: args[2],
      //   refresh: () => refreshTasksOfDocument(),
      // })
    } catch (e) {
      const error = e as AxiosError

      toast.error(
        error?.response?.data?.message ?? 'Не удалось отклонить штамп!',
      )
    }
  }

  const { checkOperation } = useCheckOperation()

  const handleSignClick = () => {
    if (documentType) {
      checkOperation({
        operationId: 'STAMP_SIGN',
        operationCallback: handleSign,
      })
    }
  }

  const handleOpenClick = (callback: () => void) => {
    if (documentType) {
      checkOperation({
        operationId: 'STAMP_SIGN',
        operationCallback: callback,
      })
    }
  }

  return (
    <Stack direction="row" justifyContent="center" spacing={4}>
      {cryptography_version === 'v1' && (
        <Button variant="contained" onClick={handleSignClick}>
          Подписать
        </Button>
      )}
      {cryptography_version === 'v2' && (
        <CryptographyWrapper
          currentUser={{
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
            surname: '',
            middleName: user?.middleName || '',
            userId: user?.userId || '',
          }}
          proxy={mrfList?.poas}
          proxyIsEnabled={poaIsEnabled}
          signContent={prepareData}
          signMode="multi"
          signType="string"
          onSuccess={signedDataTransform}
        >
          {({ onOpen }) => (
            <Button
              color="accepted"
              variant="contained"
              onClick={() => handleOpenClick(onOpen)}
            >
              Подписать
            </Button>
          )}
        </CryptographyWrapper>
      )}

      <DeviationDialog
        additionalInfo={{ id: docId, entityType: 'stamp' }}
        documentType={documentType}
        title="Отклонение штампа"
        onSubmit={(...args) => {
          onRejectStamp(args)
        }}
      />
    </Stack>
  )
}

export default SignPanel
